import type React from 'react';

/**
 * Shared inline style objects for the HITL screens. Kept as objects rather
 * than a .module.css so:
 *   1. HITL screens can ship as one PR without a Tailwind/CSS-modules setup
 *      change to instructor-dashboard.
 *   2. The design tokens from `@hauraki/ui/tokens.css` are still consumable
 *      via CSS custom properties (`var(--accent-...)`) — instructor-dashboard
 *      doesn't import the tokens CSS yet so we fall back to literal hex.
 *
 * Amber #B8600B is the HITL instructor chip color — distinct from the AI
 * tutor's violet so learner + instructor can tell turns apart at a glance
 * (Figma page 22 spec).
 */

export const HITL_AMBER = '#B8600B';
export const HITL_AMBER_LIGHT = '#F5E6D8';
export const HITL_VIOLET = '#7C3AED';
export const HITL_SURFACE = '#F7F7F7';
export const HITL_BORDER = '#E4E4E7';
export const HITL_INK = '#18181B';
export const HITL_INK_SECONDARY = '#52525B';
export const HITL_INK_MUTED = '#71717A';
export const HITL_STATUS_WARN = '#B8600B';
export const HITL_STATUS_OK = '#15803D';
export const HITL_STATUS_ALERT = '#B91C1C';

export const styles = {
  page: {
    padding: '24px',
    maxWidth: 1280,
    margin: '0 auto',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: HITL_INK,
  } as React.CSSProperties,
  h1: {
    fontSize: 28,
    fontWeight: 600,
    margin: '0 0 8px 0',
  } as React.CSSProperties,
  subtitle: {
    color: HITL_INK_SECONDARY,
    margin: '0 0 24px 0',
  } as React.CSSProperties,
  card: {
    background: '#fff',
    border: `1px solid ${HITL_BORDER}`,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  } as React.CSSProperties,
  row: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  } as React.CSSProperties,
  colGap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  } as React.CSSProperties,
  amberChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 12,
    background: HITL_AMBER_LIGHT,
    color: HITL_AMBER,
    fontSize: 12,
    fontWeight: 600,
  } as React.CSSProperties,
  violetChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 12,
    background: '#EDE9FE',
    color: HITL_VIOLET,
    fontSize: 12,
    fontWeight: 600,
  } as React.CSSProperties,
  input: {
    padding: '10px 12px',
    borderRadius: 6,
    border: `1px solid ${HITL_BORDER}`,
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  } as React.CSSProperties,
  button: {
    padding: '10px 16px',
    borderRadius: 6,
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  primary: {
    background: HITL_AMBER,
    color: '#fff',
  } as React.CSSProperties,
  secondary: {
    background: '#fff',
    color: HITL_INK,
    border: `1px solid ${HITL_BORDER}`,
  } as React.CSSProperties,
  danger: {
    background: HITL_STATUS_ALERT,
    color: '#fff',
  } as React.CSSProperties,
  muted: {
    color: HITL_INK_MUTED,
    fontSize: 12,
  } as React.CSSProperties,
  transcriptBubble: (sender: 'learner' | 'instructor' | 'ai') =>
    ({
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
      maxWidth: '80%',
      alignSelf: sender === 'learner' ? 'flex-start' : 'flex-end',
      background:
        sender === 'instructor'
          ? HITL_AMBER_LIGHT
          : sender === 'ai'
            ? '#EDE9FE'
            : '#F4F4F5',
      color: HITL_INK,
      border: `1px solid ${
        sender === 'instructor'
          ? HITL_AMBER
          : sender === 'ai'
            ? HITL_VIOLET
            : HITL_BORDER
      }`,
    }) as React.CSSProperties,
} as const;

/** Format a duration in ms as a human-readable "3m 12s" / "42s". */
export function formatDuration(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

/** Format an RFC-3339 timestamp as a locale time (e.g. "2:15 PM"). */
export function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}
